/**
 * Nutrition calculator — BMR / NEAT / EAT / TEF / TDEE, calorie target and macro split.
 *
 * Replaces the un-exported, untested copy inlined in
 * `componenets/nutrition/NewNutritionMenuDialog.js`. Formulas follow the v2 design mock.
 */

import { GoalTypes, GOAL_DEFAULT_WEEKLY_RANGE, type GoalValue } from "../enums/GoalTypes";
import { WeeklyRanges, type WeeklyRange } from "../enums/WeeklyRanges";

export type Gender = "male" | "female";

export type BmrFormula = "mifflin" | "harris" | "katch" | "cunningham";

export type CalculatorInputs = {
  gender: Gender;
  age: number;
  /** kg */
  weight: number;
  /** cm */
  height: number;
  /** body fat percentage — only used by katch / cunningham */
  bodyFat?: number;
  formula: BmrFormula;
  /** average daily steps */
  steps: number;
  /** strength sessions per week */
  workouts: number;
  /** average session length, minutes */
  workoutDuration: number;
};

export type EnergyBreakdown = {
  bmr: number;
  neat: number;
  eat: number;
  tef: number;
  tdee: number;
};

export const BMR_FORMULAS: Array<{ value: BmrFormula; label: string; needsBodyFat: boolean }> = [
  { value: "mifflin", label: "Mifflin-St Jeor", needsBodyFat: false },
  { value: "harris", label: "Harris-Benedict", needsBodyFat: false },
  { value: "katch", label: "Katch-McArdle", needsBodyFat: true },
  { value: "cunningham", label: "Cunningham", needsBodyFat: true },
];

export const STEPS_LEVELS: Array<{ value: number; label: string }> = [
  { value: 3000, label: "נמוכה מאוד (3,000)" },
  { value: 5000, label: "קלה (5,000–7,500)" },
  { value: 8000, label: "בינונית (7,500–10,000)" },
  { value: 11000, label: "גבוהה (10,000–12,500)" },
  { value: 14000, label: "גבוהה מאוד (12,500+)" },
];

export const WORKOUT_DURATIONS: number[] = [30, 45, 60, 75, 90];

// Goals and weekly ranges are the SAME enums the weight-tracking page uses
// (`enums/GoalTypes` + `enums/WeeklyRanges`) — the trainee already carries both on
// `NutritionCalculatorData`, so the calculator reads real data instead of its own scale.

export const goalTrend = (goal: GoalValue): -1 | 0 | 1 =>
  GoalTypes.find((g) => g.value === goal)?.trend ?? 0;

/** Only the ranges that point the same way as the goal. */
export const getWeeklyRangesForGoal = (goal: GoalValue): WeeklyRange[] => {
  const trend = goalTrend(goal);
  if (trend === 0) return WeeklyRanges.filter((r) => r.value === "MAINTENANCE");
  return WeeklyRanges.filter(
    (r) => r.value !== "MAINTENANCE" && (trend < 0 ? r.min < 0 : r.min > 0)
  );
};

export const defaultWeeklyRangeForGoal = (goal: GoalValue): string =>
  GOAL_DEFAULT_WEEKLY_RANGE[goal] || "MAINTENANCE";

export const getRateLabel = (goal: GoalValue): string => {
  const trend = goalTrend(goal);
  return trend < 0 ? "קצב ירידה שבועי" : trend > 0 ? "קצב עלייה שבועי" : "קצב שינוי שבועי";
};

// ── energy ──────────────────────────────────────────────────────────────────

export const leanBodyMass = (weight: number, bodyFat?: number): number =>
  weight * (1 - (bodyFat || 0) / 100);

export const calcBMR = (inputs: Partial<CalculatorInputs>): number => {
  const w = Number(inputs.weight) || 0;
  const h = Number(inputs.height) || 0;
  const a = Number(inputs.age) || 0;
  const male = (inputs.gender || "male") === "male";
  const formula = inputs.formula || "mifflin";

  if (formula === "katch" || formula === "cunningham") {
    // these need body fat; without it they can't be computed
    if (!w || !inputs.bodyFat) return 0;
    const lbm = leanBodyMass(w, inputs.bodyFat);
    return Math.round(formula === "katch" ? 370 + 21.6 * lbm : 500 + 22 * lbm);
  }

  if (!w || !h || !a) return 0;

  if (formula === "harris") {
    return Math.round(
      male
        ? 88.362 + 13.397 * w + 4.799 * h - 5.677 * a
        : 447.593 + 9.247 * w + 3.098 * h - 4.33 * a
    );
  }
  // mifflin-st jeor
  return Math.round(10 * w + 6.25 * h - 5 * a + (male ? 5 : -161));
};

/** Non-exercise activity, scaled by body weight (reference 70kg). */
export const calcNEAT = (steps: number, weight: number): number =>
  Math.round((Number(steps) || 0) * 0.045 * ((Number(weight) || 70) / 70));

/** Exercise thermogenesis, averaged over the week. */
export const calcEAT = (weight: number, workouts: number, durationMinutes: number): number =>
  Math.round(
    (5.5 * (Number(weight) || 70) * ((Number(durationMinutes) || 0) / 60) * (Number(workouts) || 0)) / 7
  );

/** Thermic effect of food — ~10% of everything else. */
export const calcTEF = (bmr: number, neat: number, eat: number): number =>
  Math.round((bmr + neat + eat) * 0.1);

export type BmrInput = "weight" | "height" | "age" | "bodyFat";

/**
 * The inputs this formula needs and hasn't got. Nothing can be computed until it is empty,
 * so the UI can mark exactly those fields instead of just showing a dash where the TDEE
 * should be. Which fields count depends on the formula: Katch / Cunningham work off lean
 * body mass and never look at height or age.
 */
export const missingBmrInputs = (inputs: Partial<CalculatorInputs>): BmrInput[] => {
  const needsBodyFat = inputs.formula === "katch" || inputs.formula === "cunningham";
  const required: BmrInput[] = needsBodyFat
    ? ["weight", "bodyFat"]
    : ["weight", "height", "age"];
  return required.filter((key) => !Number(inputs[key]));
};

export const calcEnergy = (inputs: Partial<CalculatorInputs>): EnergyBreakdown => {
  const bmr = calcBMR(inputs);
  const neat = calcNEAT(inputs.steps || 0, inputs.weight || 0);
  const eat = calcEAT(inputs.weight || 0, inputs.workouts || 0, inputs.workoutDuration || 0);
  const tef = calcTEF(bmr, neat, eat);
  return { bmr, neat, eat, tef, tdee: Math.round(bmr + neat + eat + tef) };
};

/**
 * Daily calorie target from a `WeeklyRanges` value. The range's midpoint is the planned
 * weekly kg change; 1kg of body weight ≈ 7700 kcal. The sign comes from the range itself,
 * so MAINTENANCE (-0.2…+0.2) nets out to zero.
 */
export const calcTargetCalories = (tdee: number, weeklyRangeValue: string): number => {
  if (!tdee) return 0;
  const range = WeeklyRanges.find((r) => r.value === weeklyRangeValue);
  if (!range) return tdee;
  const avgWeeklyKg = (range.min + range.max) / 2;
  return Math.round(tdee + (avgWeeklyKg * 7700) / 7);
};

// ── macros ──────────────────────────────────────────────────────────────────

export const PROTEIN_RANGE = { min: 1.2, max: 3.5, step: 0.1, default: 2.0 };
export const FAT_RANGE = { min: 0.5, max: 2.0, step: 0.05, default: 0.9 };
export const CARB_RANGE = { min: 1.0, max: 6.0, step: 0.1, default: 3.0 };
export const FIBER_RANGE = { min: 10, max: 80, step: 1 };

export type MacroRecommendation = {
  proteinG: number;
  fatG: number;
  carbsG: number;
  proteinKcal: number;
  fatKcal: number;
  carbsKcal: number;
  totalKcal: number;
  proteinPct: number;
  fatPct: number;
  carbsPct: number;
  carbsPerKg: number;
  /** totalKcal − target; 0 when carbs fill the remainder */
  diffFromTarget: number;
};

/**
 * Protein and fat are set per kg of body weight. Carbs either fill the remaining
 * calories (default) or are set per kg too when the trainer overrides them.
 */
export const recommendMacros = (opts: {
  targetCalories: number;
  weight: number;
  proteinPerKg: number;
  fatPerKg: number;
  carbsPerKg?: number;
  carbsOverride?: boolean;
}): MacroRecommendation => {
  const w = Number(opts.weight) || 0;
  const target = Number(opts.targetCalories) || 0;
  const proteinG = Math.round((Number(opts.proteinPerKg) || 0) * w);
  const fatG = Math.round((Number(opts.fatPerKg) || 0) * w);
  const proteinKcal = proteinG * 4;
  const fatKcal = fatG * 9;

  let carbsG: number;
  if (opts.carbsOverride) {
    carbsG = Math.round((Number(opts.carbsPerKg) || 0) * w);
  } else {
    // carbs fill whatever is left — never negative
    carbsG = Math.round(Math.max(0, target - proteinKcal - fatKcal) / 4);
  }
  const carbsKcal = carbsG * 4;
  const totalKcal = proteinKcal + fatKcal + carbsKcal;

  const pct = (kcal: number) => (totalKcal ? Math.round((kcal / totalKcal) * 100) : 0);

  return {
    proteinG,
    fatG,
    carbsG,
    proteinKcal,
    fatKcal,
    carbsKcal,
    totalKcal,
    proteinPct: pct(proteinKcal),
    fatPct: pct(fatKcal),
    carbsPct: pct(carbsKcal),
    carbsPerKg: w ? Math.round((carbsG / w) * 10) / 10 : 0,
    diffFromTarget: target ? totalKcal - target : 0,
  };
};

export const defaultFiber = (gender: Gender): number => (gender === "female" ? 25 : 38);

// ── prefill from the server's NutritionCalculatorData ────────────────────────

/** Maps the trainee's stored steps average onto one of the STEPS_LEVELS buckets. */
export const mapStepsToLevel = (avgSteps?: number): number => {
  const s = Number(avgSteps) || 0;
  if (!s) return 8000;
  if (s < 4000) return 3000;
  if (s < 7500) return 5000;
  if (s < 10000) return 8000;
  if (s < 12500) return 11000;
  return 14000;
};

/** Falls back to MAINTENANCE when the trainee has no goal set yet. */
export const toGoalValue = (goal?: string): GoalValue =>
  (GoalTypes.find((g) => g.value === goal)?.value as GoalValue) || "MAINTENANCE";

export const toWeeklyRangeValue = (weeklyRange: string | undefined, goal: GoalValue): string => {
  const allowed = getWeeklyRangesForGoal(goal);
  return allowed.some((r) => r.value === weeklyRange)
    ? (weeklyRange as string)
    : defaultWeeklyRangeForGoal(goal);
};

export type { GoalValue, WeeklyRange };
